import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Globe, ExternalLink, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import './NewsWidget.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NewsWidget = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [news, setNews] = useState([]);
  const [error, setError] = useState(null);

  // Fetch news from backend
  const fetchNews = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/news`);
      const data = await response.json();
      
      if (response.ok && data.status === 'success') {
        setNews(data.news || []);
        setLastUpdated(new Date(data.cached_at));
      } else {
        setError('Unable to fetch news. Please try again later.');
      }
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Failed to load news. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchNews();
  }, []);

  // Auto-refresh every 5 hours (18000000 ms)
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchNews();
    }, 18000000); // 5 hours in milliseconds

    return () => clearInterval(intervalId);
  }, []);

  const handleRefresh = () => {
    fetchNews();
  };

  const formatLastUpdated = () => {
    const now = new Date();
    const diff = Math.floor((now - lastUpdated) / 1000 / 60); // minutes
    if (diff < 60) return `${diff} minutes ago`;
    const hours = Math.floor(diff / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    return lastUpdated.toLocaleDateString();
  };

  const getTimeAgo = (publishedAt) => {
    const now = new Date();
    const published = new Date(publishedAt);
    const diffMs = now - published;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  return (
    <section id="news" className="news-section">
      <div className="news-container">
        <div className="news-header">
          <div className="news-title-wrapper">
            <Globe className="globe-icon" size={32} />
            <div>
              <h2 className="section-title">Latest Global News</h2>
              <p className="section-description">
                Real-time updates from 80,000+ sources worldwide • Auto-updates every 5 hours
              </p>
              <p className="last-updated">Last updated: {formatLastUpdated()}</p>
            </div>
          </div>
          <Button onClick={handleRefresh} className="refresh-button" disabled={isLoading}>
            <RefreshCw className={isLoading ? 'spinning' : ''} size={18} />
            Refresh
          </Button>
        </div>

        {error && (
          <div className="news-error">
            <p>{error}</p>
          </div>
        )}

        {isLoading && news.length === 0 && (
          <div className="news-loading">
            <p>Loading latest news...</p>
          </div>
        )}

        <div className="news-grid">
          {news.map((item, index) => (
            <Card key={item.id || index} className="news-card">
              <div className="news-header-content">
                <div className="news-category">{item.category || 'News'}</div>
              </div>
              <h3 className="news-title">{item.title}</h3>
              {item.description && (
                <p className="news-description">{item.description.substring(0, 120)}...</p>
              )}
              <div className="news-meta">
                <span className="news-source">{item.source}</span>
                <span className="news-time">{getTimeAgo(item.publishedAt)}</span>
              </div>
              <a href={item.url} target="_blank" rel="noopener noreferrer" className="news-link">
                Read More <ExternalLink size={14} />
              </a>
            </Card>
          ))}
        </div>

        <div className="news-sources">
          <p className="sources-text">Powered by NewsAPI - Aggregating from 80,000+ sources including:</p>
          <div className="sources-logos">
            <span>Bloomberg</span>
            <span>Reuters</span>
            <span>Financial Times</span>
            <span>BBC News</span>
            <span>Al Jazeera</span>
            <span>The Diplomat</span>
            <span>Wall Street Journal</span>
            <span>Nikkei Asia</span>
            <span>SCMP</span>
            <span>CNN</span>
            <span>The Guardian</span>
            <span>Foreign Policy</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsWidget;
